import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  toBillingPlanView,
  type BillingPlanRecord,
  type BillingPlanView,
  type PlanAudience,
} from "@/lib/billingPlans";

interface UseSubscriptionPlansResult {
  plans: BillingPlanView[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseSubscriptionPlansOptions {
  audience?: PlanAudience | "all";
}

const PLAN_SELECT =
  "id, name, slug, supplier_limit, monthly_price_pennies, annual_price_pennies, stripe_price_id_monthly, stripe_price_id_annual, stripe_product_id_monthly, stripe_product_id_annual, is_active, display_order, plan_audience, includes_supplier_plus";

export function useSubscriptionPlans(
  options: UseSubscriptionPlansOptions = {},
): UseSubscriptionPlansResult {
  const { audience = "customer" } = options;
  const [plans, setPlans] = useState<BillingPlanView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    let query = supabase
      .from("subscription_plans")
      .select(PLAN_SELECT)
      .eq("is_active", true);

    if (audience !== "all") {
      query = query.eq("plan_audience", audience);
    }

    const { data, error: queryError } = await query.order("display_order");

    if (queryError) {
      setError(queryError.message);
      setPlans([]);
      setIsLoading(false);
      return;
    }

    setPlans((data as BillingPlanRecord[] | null)?.map(toBillingPlanView) ?? []);
    setIsLoading(false);
  }, [audience]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return {
    plans,
    isLoading,
    error,
    refetch: fetchPlans,
  };
}
